/* ---------------------------------------------------------------------------
   The condition trend on the bridge record (LC-62): the whole structure, then
   each element, one small chart apiece, over the inspections on record. What
   is drawn, and what is left out, is src/data/conditionTrend.ts; the pieces
   are the charts' own (src/components/chart.ts).
   --------------------------------------------------------------------------- */

import { h } from '../core/dom.ts';
import { chartBox, r1, scale, sv } from '../components/chart.ts';
import { prettyDate, scaleMax } from '../data/derive.ts';
import { conditionTrend, type TrendSeries } from '../data/conditionTrend.ts';
import type { Inspection } from '../data/types.ts';

const W = 320;
const H = 112;
const M = { l: 26, r: 10, t: 10, b: 20 };

export function conditionTrendBlock(inspections: readonly Inspection[]): HTMLElement | null {
  const trend = conditionTrend(inspections);
  if (!trend || trend.series[0]!.points.length < 1) return null;
  const top = scaleMax(trend.scale) ?? Math.max(1, ...trend.series.flatMap((s) => s.points.map((p) => p.value)));
  const dates = trend.series.flatMap((s) => s.points.map((p) => Date.parse(p.date)));
  const t0 = Math.min(...dates);
  const t1 = Math.max(...dates);
  return h(
    'div',
    { class: 'ctrend', 'data-series': String(trend.series.length) },
    h('div', {
      class: 'works__sub-head',
      text: `Condition trend · ${trend.scale}`,
    }),
    h(
      'div',
      { class: 'ctrend__grid', style: 'display:grid;grid-template-columns:repeat(auto-fill,minmax(240px,1fr));gap:8px;padding:0 14px 12px' },
      ...trend.series.map((series) => trendChart(series, top, t0, t1)),
    ),
  );
}

function trendChart(series: TrendSeries, top: number, t0: number, t1: number): HTMLElement {
  const x = scale(t0, t1 === t0 ? t0 + 1 : t1, M.l, W - M.r);
  const y = scale(0, top, H - M.b, M.t);
  const pts = series.points.map((p) => ({ ...p, px: x(Date.parse(p.date)), py: y(p.value) }));
  const ticks = [0, top / 2, top].map((v) => Math.round(v * 10) / 10);
  const svg = sv(
    'svg',
    {
      viewBox: `0 0 ${W} ${H}`,
      class: 'pchart__svg',
      role: 'img',
      'data-chart': 'condition',
      'aria-label': `${series.label}: ${series.points.map((p) => `${p.value} on ${p.date}`).join(', ')}`,
    },
    ...ticks.map((v) =>
      sv(
        'g',
        {},
        sv('line', { x1: M.l, x2: W - M.r, y1: r1(y(v)), y2: r1(y(v)), class: 'pchart__grid' }),
        sv('text', { x: M.l - 5, y: y(v) + 3.5, class: 'pchart__tick', 'text-anchor': 'end' }, String(v)),
      ),
    ),
    sv('text', { x: M.l, y: H - 5, class: 'pchart__tick' }, pts[0]!.date.slice(0, 4)),
    pts.length > 1
      ? sv('text', { x: W - M.r, y: H - 5, class: 'pchart__tick', 'text-anchor': 'end' }, pts[pts.length - 1]!.date.slice(0, 4))
      : null,
    pts.length > 1
      ? sv('path', { d: `M${pts.map((p) => `${r1(p.px)},${r1(p.py)}`).join('L')}`, class: 'pchart__line pchart__line--actual' })
      : null,
    ...pts.map((p) =>
      sv('circle', {
        cx: r1(p.px),
        cy: r1(p.py),
        r: 3.5,
        class: 'pchart__dot pchart__dot--actual',
        'data-point': String(p.value),
        'data-tip': `${series.label}\n${prettyDate(p.date)}\nCondition ${p.value}`,
      }),
    ),
  );
  return h(
    'div',
    { class: 'ctrend__one', 'data-trend': series.key },
    h('div', { class: 'ctrend__title', style: 'font-size:12px;color:var(--txt2);margin-bottom:2px', text: series.label }),
    chartBox(svg),
  );
}
